import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Spin } from 'antd';
import { useAuthStore, roleDefaultRoutes } from '@/stores/authStore';
import { normalizeUserRole } from '@/utils/roleView';
import type { UserRole } from '@/types';

const normalizePermission = (permission: string): string =>
  permission.replace(':', '.').toLowerCase();

// FLOW[26998]: MS229 - Enhanced ProtectedRoute with permission-based access control
interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  /** Permission code required to access this route (e.g., 'stock:view') */
  requiredPermission?: string;
  /** Alternative permissions - access granted if user has ANY of these */
  anyPermission?: string[];
}

export function ProtectedRoute({ children, allowedRoles, requiredPermission, anyPermission }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading, activeWorkspace } = useAuthStore();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // FLOW[47391]: a personal bearer is a separate product boundary, not a
  // visual filter over production ERP pages.
  const personalWorkspacePath = '/v2/accounting/personal-1c';
  if (
    activeWorkspace?.kind === 'personal_finance' &&
    location.pathname !== personalWorkspacePath
  ) {
    return <Navigate to={personalWorkspacePath} replace />;
  }
  if (
    activeWorkspace?.kind === 'production' &&
    location.pathname === personalWorkspacePath
  ) {
    const productionRole = user.roles?.[0] || user.role;
    const productionRoute = roleDefaultRoutes[productionRole] || '/v2/dashboard';
    return <Navigate to={productionRoute} replace />;
  }

  // Check if user has pending status (for partners)
  if (user.status === 'pending') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: 24,
        textAlign: 'center'
      }}>
        <h2>Ваша заявка на рассмотрении</h2>
        <p>Пожалуйста, дождитесь подтверждения администратора.</p>
      </div>
    );
  }

  // FLOW[23639]: MS202 - Fixed to use roles[0] instead of role
  const rawRole = user.roles?.[0] || user.role;
  const userRole = normalizeUserRole(rawRole);
  if (!userRole) {
    return (
      <div role="alert" style={{ padding: 24 }}>
        <h2>Роль пользователя не определена</h2>
        <p>Обратитесь к администратору для восстановления доступа.</p>
      </div>
    );
  }
  const userPermissions = (user.permissions || []).map(normalizePermission);

  // FLOW[26999]: MS229 - Check permission-based access
  // Permission check takes precedence over role check
  if (requiredPermission) {
    const required = normalizePermission(requiredPermission);
    const hasPermission = userPermissions.includes(required) || userRole === 'admin';
    if (!hasPermission) {
      const defaultRoute = roleDefaultRoutes[userRole as UserRole] || '/v2/dashboard';
      return <Navigate to={defaultRoute} replace />;
    }
    return <>{children}</>;
  }

  // FLOW[27000]: MS229 - Check any of multiple permissions
  if (anyPermission && anyPermission.length > 0) {
    const hasAnyPermission =
      anyPermission.some((permission) =>
        userPermissions.includes(normalizePermission(permission)),
      ) || userRole === 'admin';
    if (!hasAnyPermission) {
      const defaultRoute = roleDefaultRoutes[userRole as UserRole] || '/v2/dashboard';
      return <Navigate to={defaultRoute} replace />;
    }
    return <>{children}</>;
  }

  // Check if user's role is allowed for this route (fallback to role-based)
  if (allowedRoles && !allowedRoles.includes(userRole as UserRole)) {
    // Redirect to user's default dashboard
    const defaultRoute = roleDefaultRoutes[userRole as UserRole] || '/v2/dashboard';
    return <Navigate to={defaultRoute} replace />;
  }

  // Handle root/dashboard redirect based on role
  if (location.pathname === '/' || location.pathname === '/dashboard') {
    const defaultRoute = roleDefaultRoutes[userRole as UserRole] || '/v2/dashboard';
    return <Navigate to={defaultRoute} replace />;
  }

  return <>{children}</>;
}
